/**
 * Audit Log Page Component
 * Part of TASK-334: Frontend Admin Panel
 *
 * Main page for viewing, filtering, and exporting audit logs.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FileText, RefreshCw, AlertTriangle, BarChart2 } from 'lucide-react';
import { auditService } from '../../services/admin';
import type {
  AuditLogEntry,
  AuditLogListResponse,
  AuditFilter,
} from '../../types/admin';
import {
  AuditFilters,
  AuditLogTable,
  AuditExportButton,
  AuditDetailModal,
} from '../../components/admin/audit';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const DEFAULT_PAGE_SIZE = 20;

const AuditPage: React.FC = () => {
  // State
  const [logsData, setLogsData] = useState<AuditLogListResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<AuditFilter>({
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
  });

  // Modal state
  const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Quick stats
  const [stats, setStats] = useState<{
    totalLogs: number;
    warningsAndErrors: number;
    uniqueUsers: number;
  } | null>(null);

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await auditService.listLogs(filter);
      setLogsData(data);

      // Calculate quick stats from first fetch
      if (!stats && data.logs.length > 0) {
        const warningsAndErrors = data.logs.filter(
          (l) => l.severity === 'warning' || l.severity === 'error' || l.severity === 'critical'
        ).length;
        const uniqueUsers = new Set(data.logs.filter((l) => l.user_id).map((l) => l.user_id)).size;
        setStats({
          totalLogs: data.total,
          warningsAndErrors,
          uniqueUsers,
        });
      }
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setIsLoading(false);
    }
  }, [filter, stats]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Handlers
  const handleFilterChange = (newFilter: AuditFilter) => {
    setFilter(newFilter);
  };

  const handleClearFilters = () => {
    setFilter({
      page: 1,
      page_size: DEFAULT_PAGE_SIZE,
    });
    setStats(null);
  };

  const handlePageChange = (page: number) => {
    setFilter((prev) => ({ ...prev, page }));
  };

  const handleViewDetails = (entry: AuditLogEntry) => {
    setSelectedEntry(entry);
    setShowDetailModal(true);
  };

  const handleRefresh = () => {
    setStats(null);
    fetchLogs();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
          <p className="text-gray-500 mt-1">
            View and export system activity logs for compliance and security
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handleRefresh} disabled={isLoading}>
            <RefreshCw size={16} className={`mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <AuditExportButton filter={filter} disabled={isLoading} />
        </div>
      </div>

      {/* Quick stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <FileText size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {stats.totalLogs.toLocaleString()}
              </p>
              <p className="text-sm text-gray-500">Total Log Entries</p>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <AlertTriangle size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {stats.warningsAndErrors}
              </p>
              <p className="text-sm text-gray-500">Warnings & Errors (visible)</p>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <BarChart2 size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {stats.uniqueUsers}
              </p>
              <p className="text-sm text-gray-500">Active Users (visible)</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <AuditFilters
        filter={filter}
        onFilterChange={handleFilterChange}
        onClear={handleClearFilters}
      />

      {/* Log table */}
      <AuditLogTable
        data={logsData}
        isLoading={isLoading}
        page={filter.page || 1}
        onPageChange={handlePageChange}
        onViewDetails={handleViewDetails}
      />

      {/* Detail modal */}
      <AuditDetailModal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedEntry(null);
        }}
        entry={selectedEntry}
      />

      {/* Info note */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <FileText size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium">About Audit Logs</p>
          <p className="mt-1">
            Audit logs are immutable records of system activity. They include user actions,
            security events, data changes, and access attempts. Logs are retained according
            to your account's compliance requirements.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuditPage;
